import {
  GeneralSelect,
  StyledText,
  Tooltip,
  Input,
  useFrontEndEventTrackingService,
} from '@clickcease/cc-client-components-lib'
import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { forwardRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetUserDomains } from '../DomainPresentation/api/getUserDomains'

import { useDeleteMicrosoftAudience } from './api/deleteMicrosoftAudience'
import {
  TagListContainer,
  LabelWithTooltip,
  SelectionContainer,
  TagsSelectionRow,
  InputContainer,
  RemoveButton,
  TagsListTable,
} from './MicrosoftAudienceModal.styled'
import { AudienceTagSelection, validateInputOnlyNumbers } from './MicrosoftAudienceModal.utils'

interface TagListProps {
  tags: AudienceTagSelection[]
  setTags: (a: AudienceTagSelection[]) => void
  editMode: boolean
}

export const AudienceFormsList = forwardRef(
  (props: TagListProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { tags, setTags, editMode } = props
    const [tagError, setTagError] = useState(false)
    const { data } = useGetUserDomains({})
    const { t } = useTranslation('PMPlatforms')
    const { track } = useFrontEndEventTrackingService()
    const { mutate: deleteTagMutation } = useDeleteMicrosoftAudience()

    const domains = useMemo(
      () =>
        !data?.domains
          ? []
          : data.domains.map((domain) => ({ value: `${domain.id}`, label: domain.address })),
      [data]
    )

    const deleteTag = (deletedIndex: number, id: number | undefined) => () => {
      if (id) {
        // tags from backend have id
        deleteTagMutation({ audienceId: id })
        track(['dataLayer', 'segment'], 'Microsoft.RemoveConnection', {})
      }
      // created, but not submitted tags have no id and deleted by index
      setTags([...tags.filter((_, idx) => deletedIndex !== idx)])
    }

    const setTagId = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputIsValid = validateInputOnlyNumbers(e)
      const value = e.target.value

      if (inputIsValid) {
        const newTags = [...tags]
        // UET tag is max 9 digits
        newTags[index].uetTag = value.length < 9 ? value : value.slice(0, 9)
        setTags(newTags)
        // reset error if input is valid
        if (tagError) setTagError(false)
      } else {
        setTagError(true)
      }
    }

    const setTagDomainId = (index: number, value: string) => {
      const newTags = [...tags]
      newTags[index].domainId = Number(value)
      setTags(newTags)
    }

    // added to react select to fix the issue with the select menu not showing on top of the modal
    document.body.style.pointerEvents = 'auto'

    return (
      <TagsListTable>
        <TagsSelectionRow css={{ borderBottom: '1px solid $lightNeutral', paddingBottom: '$6' }}>
          <StyledText variant={'baseBold'}>{t('microsoft-audience-modal.domain-title')}</StyledText>
          <LabelWithTooltip>
            <StyledText variant={'baseBold'}>{t('microsoft-audience-modal.tag')}</StyledText>
            <Tooltip
              tooltiptContent={t('microsoft-audience-modal.tag-tooltip')}
              trigerStyle={{ width: '16px', height: '16px' }}
            />
          </LabelWithTooltip>
          <StyledText variant={'baseBold'}></StyledText>
        </TagsSelectionRow>
        <TagListContainer>
          {tags.map((opt, index) => (
            <TagsSelectionRow
              key={`TagsSelectionRow${index}`}
              ref={index === tags.length - 1 ? ref : undefined}
            >
              <SelectionContainer>
                <GeneralSelect
                  wrapperCSS={{ width: '250px' }}
                  value={opt.domainId && domains.find((d) => d.value === opt?.domainId?.toString())}
                  options={domains}
                  onChange={(item) => {
                    if (item) setTagDomainId(index, item.value)
                  }}
                  menuPortalTarget={document.body}
                />
              </SelectionContainer>
              <InputContainer>
                <Input
                  value={opt.uetTag}
                  onChange={setTagId(index)}
                  type="numbers"
                  error={tagError}
                />
              </InputContainer>
              {!editMode && (
                <RemoveButton onClick={deleteTag(index, opt.id)}>
                  <FontAwesomeIcon icon={faTrashCan as IconProp} />
                </RemoveButton>
              )}
            </TagsSelectionRow>
          ))}
        </TagListContainer>
      </TagsListTable>
    )
  }
)

AudienceFormsList.displayName = 'AudienceFormsList'
